import React, { useState } from "react";
import Section from "./layouts/Section";
import { SiteWide } from "./customComponents";
import { useSelector, useDispatch } from 'react-redux';

import { authActions } from '../redux/actions';
import { userSelect, allRemoteSettingsSelect } from '../redux/selectors/index';
import { notify } from '../utility/Helper1';

// MUI icons
import KeyIcon from "@mui/icons-material/Key";
import ShieldIcon from "@mui/icons-material/Shield";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";

function ChangePassword() {
    const userData = useSelector(userSelect);
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Updating password...');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [withdrawalPin, setWithdrawalPin] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showPin, setShowPin] = useState(false);

    const handlePasswordChange = async (e) => {
        e.preventDefault();

        if (!password || !confirmPassword) { notify('Please fill in all password fields', 0); return; }
        if (password !== confirmPassword) { notify('Passwords do not match', 0); return; }
        if (password.length < 8) { notify('Password must be at least 8 characters long', 0); return; }
        if (!withdrawalPin) { notify('Please enter your PIN', 0); return; }

        setLoading(true);
        dispatch(authActions.onLogin("account/change_password", { password, withdrawalPin, googleAuth: 0 }, response => {
            if (response.code === 1) { setPassword(''); setConfirmPassword(''); setWithdrawalPin(''); }
            notify(response.msg, response.code);
            setLoading(false);
        }));
    };

    // ── shared styles ──────────────────────────────────────────────────────────
    const inputWrap = { position: "relative", marginBottom: 0 };

    const inputStyle = {
        width: "100%", borderRadius: 12, padding: "13px 44px 13px 14px",
        border: "1px solid rgba(59,130,246,0.15)",
        background: "#f8fafc", color: "#0f172a",
        fontSize: 14, fontFamily: "'DM Sans',sans-serif",
        outline: "none", boxSizing: "border-box",
        transition: "border 0.2s"
    };

    const labelStyle = {
        color: "#334155", fontSize: 12, fontWeight: 600,
        fontFamily: "'DM Sans',sans-serif",
        display: "block", marginBottom: 6
    };

    const eyeBtn = {
        position: "absolute", right: 12, top: "50%",
        transform: "translateY(-50%)", background: "none",
        border: "none", cursor: "pointer", color: "#94a3b8",
        display: "flex", alignItems: "center", padding: 0
    };

    const tips = [
        { text: "Use uppercase, lowercase, numbers & special characters" },
        { text: "Don't reuse passwords from other accounts" },
        { text: "Change your password periodically" },
        { text: "Use a password manager to stay safe" },
    ];

    return (
        <SiteWide id="main" title="Change Password">
            <Section loading={loading} overlayText={loadingText} title="Change Password">
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                    {/* ── Header card ── */}
                    <div style={{
                        borderRadius: 16, padding: 20, position: "relative", overflow: "hidden",
                        background: "linear-gradient(135deg,#1e3a8a 0%,#2563eb 50%,#3b82f6 100%)",
                        border: "1px solid rgba(96,165,250,0.15)"
                    }}>
                        <div style={{
                            position: "absolute", top: 0, right: 0, opacity: 0.12,
                            width: 140, height: 140,
                            background: "radial-gradient(circle,#93c5fd,transparent)",
                            borderRadius: "50%", transform: "translate(30px,-30px)"
                        }} />
                        <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative" }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 14,
                                background: "rgba(255,255,255,0.15)",
                                backdropFilter: "blur(8px)",
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                            }}>
                                <KeyIcon style={{ color: "#ffffff", fontSize: 24 }} />
                            </div>
                            <div>
                                <p style={{ color: "#ffffff", fontSize: 16, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                    Change Password
                                </p>
                                <p style={{ color: "rgba(219,234,254,0.7)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                    Keep your account secure with a strong password
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ── Form card ── */}
                    <div style={{
                        background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                        boxShadow: "0 4px 18px rgba(59,130,246,0.04)", borderRadius: 16, padding: 20
                    }}>
                        <form onSubmit={handlePasswordChange} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                            {/* New password */}
                            <div>
                                <label style={labelStyle}>New Password</label>
                                <div style={inputWrap}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter new password"
                                        style={inputStyle}
                                    />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} style={eyeBtn}>
                                        {showPassword
                                            ? <VisibilityOffIcon style={{ fontSize: 18 }} />
                                            : <VisibilityIcon style={{ fontSize: 18 }} />}
                                    </button>
                                </div>
                                <p style={{ color: "#94a3b8", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: "5px 0 0" }}>
                                    Must be at least 8 characters long
                                </p>
                            </div>

                            {/* Confirm password */}
                            <div>
                                <label style={labelStyle}>Confirm Password</label>
                                <div style={inputWrap}>
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Re-enter new password"
                                        style={{
                                            ...inputStyle,
                                            borderColor: confirmPassword && confirmPassword !== password
                                                ? "rgba(239,68,68,0.4)" : "rgba(59,130,246,0.15)"
                                        }}
                                    />
                                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={eyeBtn}>
                                        {showConfirmPassword
                                            ? <VisibilityOffIcon style={{ fontSize: 18 }} />
                                            : <VisibilityIcon style={{ fontSize: 18 }} />}
                                    </button>
                                </div>
                                {confirmPassword && confirmPassword !== password && (
                                    <p style={{ color: "#ef4444", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: "5px 0 0" }}>
                                        Passwords do not match
                                    </p>
                                )}
                            </div>

                            {/* PIN */}
                            <div>
                                <label style={labelStyle}>PIN Verification</label>
                                <div style={inputWrap}>
                                    <input
                                        type={showPin ? 'text' : 'password'}
                                        value={withdrawalPin}
                                        onChange={(e) => setWithdrawalPin(e.target.value)}
                                        maxLength={4}
                                        placeholder="4-digit withdrawal PIN"
                                        style={inputStyle}
                                    />
                                    <button type="button" onClick={() => setShowPin(!showPin)} style={eyeBtn}>
                                        {showPin
                                            ? <VisibilityOffIcon style={{ fontSize: 18 }} />
                                            : <VisibilityIcon style={{ fontSize: 18 }} />}
                                    </button>
                                </div>
                                <p style={{ color: "#94a3b8", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: "5px 0 0" }}>
                                    Enter your 4-digit PIN to confirm
                                </p>
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    width: "100%", borderRadius: 12, padding: "13px 0", border: "none",
                                    background: loading
                                        ? "rgba(59,130,246,0.3)"
                                        : "linear-gradient(135deg,#3b82f6,#2563eb)",
                                    color: "#fff", fontSize: 14, fontWeight: 700,
                                    fontFamily: "'Syne',sans-serif", cursor: loading ? "not-allowed" : "pointer",
                                    boxShadow: loading ? "none" : "0 4px 16px rgba(59,130,246,0.25)",
                                    marginTop: 4
                                }}
                            >
                                {loading ? 'Updating...' : 'Update Password'}
                            </button>
                        </form>
                    </div>

                    {/* ── Security tips ── */}
                    <div style={{
                        background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                        boxShadow: "0 2px 12px rgba(59,130,246,0.02)", borderRadius: 16, padding: 20
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: 10,
                                background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.15)",
                                display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                                <ShieldIcon style={{ color: "#22c55e", fontSize: 18 }} />
                            </div>
                            <p style={{ color: "#0f172a", fontSize: 13, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                Security Tips
                            </p>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {tips.map((tip, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                                    <CheckCircleIcon style={{ color: "#22c55e", fontSize: 15, marginTop: 1, flexShrink: 0 }} />
                                    <p style={{ color: "#475569", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0, lineHeight: 1.5 }}>
                                        {tip.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </Section>
        </SiteWide>
    );
}

export default ChangePassword;